import React from "react";
import { useTranslation } from "react-i18next";
import { MdSchedule } from "react-icons/md";
import ChannelLogo, { ChannelNameCell } from "./ChannelLogo";
import { SOON_COUNT, SOON_GROUPS } from "../salesChannels";

const ComingSoonChannels = ({ view, viewToggle }) => {
  const { t } = useTranslation("settings");
  return (
    <section className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <MdSchedule className="h-[18px] w-[18px] text-brand" />
          <h2 className="m-[0px] text-base font-semibold text-ui-text">
            {t("marketplaces.soon.title")}
          </h2>
          {/* ring-1 draws the pill's edge: on the light page, bg-brand-light is
              almost the page colour. It is box-shadow based, so the dark-mode
              shim's border-colour override can't grey it out. */}
          <span className="rounded-full bg-brand-light px-2 py-px text-[11px] font-semibold text-brand ring-1 ring-inset ring-state-info-border">
            {SOON_COUNT}
          </span>
        </div>
        <div className="hidden sm:block">{viewToggle}</div>
      </div>

      {/* Phones always get the cards; the list view is too wide for them. */}
      <div
        className={`flex-col gap-[20px] ${view === "cards" ? "flex" : "flex sm:hidden"}`}
      >
        {SOON_GROUPS.map((group) => (
          <div key={group.title} className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-state-neutral-text">
                {t(group.i18nKey, { defaultValue: group.title })}
              </span>
              <span className="text-[11px] text-ui-text-subtle">
                {group.items.length}
              </span>
              <span className="h-px flex-1 bg-ui-border" />
            </div>
            <div className="grid grid-cols-2 gap-[8px] sm:grid-cols-[repeat(auto-fill,minmax(250px,1fr))] sm:gap-2.5">
              {group.items.map((channel) => (
                <div
                  key={channel.key}
                  className="flex items-start gap-2.5 rounded-[10px] border-[1px] border-ui-border bg-ui-surface px-2.5 py-[8px] sm:px-3 sm:py-2.5"
                >
                  <ChannelLogo
                    src={channel.logo}
                    className="hidden h-[38px] w-[60px] rounded-lg border-ui-border sm:flex"
                    imgClassName="max-h-[70%] max-w-[70%]"
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                    <div className="text-[13px] font-semibold leading-[1.3] text-ui-text">
                      {channel.name}
                    </div>
                    <div className="text-[11px] leading-[1.45] text-ui-text-muted [text-wrap:pretty]">
                      {t(`marketplaces.soon.channels.${channel.key}`, {
                        defaultValue: channel.desc,
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {view === "list" && (
        <div className="hidden overflow-hidden rounded-xl border-[1px] border-ui-border bg-ui-surface sm:block">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] border-collapse">
              <thead>
                <tr className="bg-ui-surface-sunken text-left text-[11px] text-ui-text-muted">
                  <th className="border-b border-ui-border px-3.5 py-2.5 font-medium">
                    {t("marketplaces.soon.platform")}
                  </th>
                  <th className="w-3/5 min-w-[240px] border-b border-ui-border px-3.5 py-2.5 font-medium">
                    {t("marketplaces.soon.whatItDoes")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {SOON_GROUPS.map((group) => (
                  <React.Fragment key={group.title}>
                    <tr className="border-t border-ui-border bg-ui-surface-sunken">
                      <td colSpan={2} className="px-3.5 py-[7px]">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-state-neutral-text">
                          {t(group.i18nKey, { defaultValue: group.title })}
                        </span>
                        <span className="ml-2 text-[11px] text-ui-text-subtle">
                          {group.items.length}
                        </span>
                      </td>
                    </tr>
                    {group.items.map((channel) => (
                      <tr
                        key={channel.key}
                        className="border-t border-ui-border-subtle hover:bg-ui-row-hover"
                      >
                        <td className="px-3.5 py-2">
                          <ChannelNameCell channel={channel} />
                        </td>
                        <td className="px-3.5 py-2 text-xs leading-[1.5] text-state-neutral-text">
                          {t(`marketplaces.soon.channels.${channel.key}`, {
                            defaultValue: channel.desc,
                          })}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

export default ComingSoonChannels;
